import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getVineyard, getVineyardRatings, rateVineyard, getWines, addFavorite, removeFavorite, checkFavorite, deleteVineyard, adminToggleVineyardFeatured } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { StarRating, WineScore } from '../components/StarRating';
import { ShareButtons } from '../components/ShareButtons';
import { toast } from 'sonner';
import { 
  MapPin, Clock, Phone, Globe, Heart, Edit, Trash2, ArrowLeft, 
  Wine, Play, Image as ImageIcon, Star, User, Calendar, Sparkles
} from 'lucide-react';

const VineyardDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isApproved, user, loading: authLoading } = useAuth();
  const [vineyard, setVineyard] = useState(null);
  const [wines, setWines] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [featuredSaving, setFeaturedSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [vineyardData, winesData, ratingsData] = await Promise.all([
        getVineyard(id),
        getWines({ vineyard_id: id }),
        getVineyardRatings(id),
      ]);
      setVineyard(vineyardData);
      setWines(winesData);
      setRatings(ratingsData);

      if (isAuthenticated) {
        const favStatus = await checkFavorite(id);
        setIsFavorite(favStatus);
        // Check if user already rated
        const existingRating = ratingsData.find(r => r.user_id === user?.id);
        if (existingRating) {
          setUserRating(existingRating.rating);
          setRatingComment(existingRating.comment);
        }
      }
    } catch (error) {
      console.error('Failed to load vineyard:', error);
      toast.error('Failed to load vineyard');
      navigate('/vineyards');
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, user?.id, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to save favorites');
      return;
    }
    if (!isApproved) {
      toast.error('Your account is pending admin approval');
      return;
    }
    try {
      if (isFavorite) {
        await removeFavorite(id);
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await addFavorite(id, 'vineyard');
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleRate = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to rate');
      return;
    }
    if (userRating === 0) {
      toast.error('Please select a rating');
      return;
    }
    setSubmittingRating(true);
    try {
      await rateVineyard(id, { vineyard_id: id, rating: userRating, comment: ratingComment });
      toast.success('Rating submitted');
      loadData();
    } catch (error) {
      toast.error('Failed to submit rating');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteVineyard(id);
      toast.success('Vineyard deleted');
      navigate('/vineyards');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#d4af37]" />
      </div>
    );
  }

  if (!vineyard) return null;

  const isOwner = user?.id === vineyard.created_by || user?.is_admin;
  const isAdmin = !!user?.is_admin;
  const isFeatured = !!vineyard?.is_featured;

  const handleToggleFeatured = async () => {
    setFeaturedSaving(true);
    try {
      await adminToggleVineyardFeatured(id, !isFeatured);
      setVineyard((prev) => ({ ...prev, is_featured: !isFeatured }));
      toast.success(
        !isFeatured
          ? `Added to Featured Vineyards on Home`
          : `Removed from Featured Vineyards`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update featured status');
    } finally {
      setFeaturedSaving(false);
    }
  };
  const heroImage = vineyard.photos?.[0] || 'https://images.unsplash.com/photo-1695700762236-3c61c5026858?w=1200&q=80';

  return (
    <div data-testid="vineyard-detail-page">
      {/* Hero Section */}
      <div className="relative h-[50vh] md:h-[60vh]">
        <img
          src={heroImage}
          alt={vineyard.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/50 to-transparent" />
        
        {/* Back Button */}
        <Link
          to="/vineyards"
          className="absolute top-8 left-8 flex items-center gap-2 text-stone-300 hover:text-white transition-colors z-10"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Vineyards
        </Link>

        {/* Actions */}
        <div className="absolute top-8 right-8 flex items-center gap-3 z-10">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleFavorite}
            className={`rounded-full bg-black/30 backdrop-blur-sm ${isFavorite ? 'text-red-500' : 'text-white'}`}
            data-testid="favorite-btn"
          >
            <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
          </Button>
          <div className="rounded-full bg-black/30 backdrop-blur-sm">
            <ShareButtons title={vineyard?.name} testId="vineyard-share-btn" />
          </div>
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleToggleFeatured}
              disabled={featuredSaving}
              title={isFeatured ? 'Remove from Featured' : 'Feature on Home'}
              className={`rounded-full bg-black/30 backdrop-blur-sm ${isFeatured ? 'text-[#d4af37]' : 'text-white'}`}
              data-testid="feature-toggle-btn"
            >
              <Sparkles className={`w-5 h-5 ${isFeatured ? 'fill-current' : ''}`} />
            </Button>
          )}
          {isOwner && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/vineyards/${id}/edit`)}
                className="rounded-full bg-black/30 backdrop-blur-sm text-white"
                data-testid="edit-btn"
              >
                <Edit className="w-5 h-5" />
              </Button>
              <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full bg-black/30 backdrop-blur-sm text-red-400"
                    data-testid="delete-btn"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-stone-900 border-stone-700">
                  <DialogHeader>
                    <DialogTitle className="text-stone-50">Delete Vineyard</DialogTitle>
                  </DialogHeader>
                  <p className="text-stone-400">
                    Are you sure you want to delete "{vineyard.name}"? This action cannot be undone.
                  </p>
                  <div className="flex gap-4 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteDialog(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDelete}
                      className="flex-1"
                      data-testid="confirm-delete-btn"
                    >
                      Delete
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="container mx-auto">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#d4af37] mb-3">
              <MapPin className="w-4 h-4" />
              {vineyard.region}, {vineyard.country}
            </div>
            <h1 className="text-4xl md:text-6xl font-serif text-stone-50 mb-3">
              {vineyard.name}
            </h1>
            <p className="text-xl text-stone-400 mb-4">{vineyard.producer}</p>
            <div className="flex items-center gap-4">
              <StarRating rating={vineyard.average_rating || 0} readonly size="lg" />
              <span className="text-stone-500">
                ({vineyard.rating_count} {vineyard.rating_count === 1 ? 'review' : 'reviews'})
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="about" className="w-full">
              <TabsList className="bg-stone-900/50 border border-stone-700 p-1 mb-8">
                <TabsTrigger value="about" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-stone-950" data-testid="tab-about">
                  About
                </TabsTrigger>
                <TabsTrigger value="wines" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-stone-950" data-testid="tab-wines">
                  Wines ({wines.length})
                </TabsTrigger>
                <TabsTrigger value="gallery" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-stone-950" data-testid="tab-gallery">
                  Gallery
                </TabsTrigger>
                <TabsTrigger value="reviews" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-stone-950" data-testid="tab-reviews">
                  Reviews
                </TabsTrigger>
              </TabsList>

              <TabsContent value="about" className="space-y-8">
                {vineyard.description && (
                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-[#d4af37] mb-4">Description</h3>
                    <p className="text-stone-400 leading-relaxed">{vineyard.description}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="wines">
                {wines.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {wines.map((wine) => (
                      <Link
                        key={wine.id}
                        to={`/wines/${wine.id}`}
                        className="wine-card group p-5 bg-stone-900/30 hover:bg-stone-900/60 border border-white/5 transition-all"
                        data-testid={`wine-card-${wine.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-xs uppercase tracking-widest text-[#d4af37] mb-1">
                              {wine.grape_variety}
                            </p>
                            <h4 className="text-lg font-serif text-stone-50 group-hover:text-[#d4af37] transition-colors">
                              {wine.name}
                            </h4>
                            {wine.vintage && (
                              <p className="text-sm text-stone-500 mt-1">{wine.vintage}</p>
                            )}
                          </div>
                          {wine.average_rating > 0 && (
                            <WineScore score={wine.average_rating} size="sm" />
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Wine className="w-12 h-12 text-stone-700 mx-auto mb-4" />
                    <p className="text-stone-500">No wines added yet</p>
                    {isAuthenticated && (
                      <Button
                        onClick={() => navigate(`/wines/new?vineyard=${id}`)}
                        className="mt-4 bg-[#d4af37] text-stone-950"
                        data-testid="add-wine-btn"
                      >
                        Add Wine
                      </Button>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="gallery">
                {vineyard.photos?.length > 0 || vineyard.videos?.length > 0 ? (
                  <div className="space-y-8">
                    {vineyard.photos?.length > 0 && (
                      <div>
                        <h3 className="text-xs uppercase tracking-widest text-[#d4af37] mb-4 flex items-center gap-2">
                          <ImageIcon className="w-4 h-4" /> Photos
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {vineyard.photos.map((photo, index) => (
                            <button
                              key={`photo-${photo}-${index}`}
                              onClick={() => setSelectedPhoto(photo)}
                              className="aspect-square overflow-hidden bg-stone-900 border border-white/5 hover:border-[#d4af37]/30 transition-colors"
                              data-testid={`photo-${index}`}
                            >
                              <img
                                src={photo}
                                alt={`${vineyard.name} photo ${index + 1}`}
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {vineyard.videos?.length > 0 && (
                      <div>
                        <h3 className="text-xs uppercase tracking-widest text-[#d4af37] mb-4 flex items-center gap-2">
                          <Play className="w-4 h-4" /> Videos
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {vineyard.videos.map((video, index) => (
                            <div key={`video-${video}-${index}`} className="aspect-video bg-stone-900 border border-white/5">
                              <video
                                src={video}
                                controls
                                className="w-full h-full object-cover"
                                data-testid={`video-${index}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ImageIcon className="w-12 h-12 text-stone-700 mx-auto mb-4" />
                    <p className="text-stone-500">No media added yet</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="reviews" className="space-y-8">
                {/* Rate Form */}
                {isAuthenticated && (
                  <div className="p-6 bg-stone-900/30 border border-white/5">
                    <h3 className="text-lg font-serif text-stone-50 mb-4">Rate this Vineyard</h3>
                    <div className="flex items-center gap-4 mb-4">
                      <StarRating rating={userRating} onRate={setUserRating} size="lg" />
                      <span className="text-stone-500">{userRating > 0 ? `${userRating}/5` : 'Select rating'}</span>
                    </div>
                    <Textarea
                      placeholder="Share your experience (optional)"
                      value={ratingComment}
                      onChange={(e) => setRatingComment(e.target.value)}
                      className="bg-stone-900/50 border-stone-700 mb-4"
                      rows={3}
                      data-testid="rating-comment"
                    />
                    <Button
                      onClick={handleRate}
                      disabled={submittingRating || userRating === 0}
                      className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f]"
                      data-testid="submit-rating-btn"
                    >
                      {submittingRating ? 'Submitting...' : 'Submit Rating'}
                    </Button>
                  </div>
                )}

                {/* Reviews List */}
                {ratings.length > 0 ? (
                  <div className="space-y-4">
                    {ratings.map((rating) => (
                      <div key={rating.id} className="p-5 bg-stone-900/20 border border-white/5" data-testid={`review-${rating.id}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center">
                              <User className="w-5 h-5 text-stone-500" />
                            </div>
                            <div>
                              <p className="text-stone-200 font-medium">{rating.user_name}</p>
                              <p className="text-xs text-stone-600">
                                {new Date(rating.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <StarRating rating={rating.rating} readonly size="sm" />
                        </div>
                        {rating.comment && (
                          <p className="text-stone-400 text-sm">{rating.comment}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Star className="w-12 h-12 text-stone-700 mx-auto mb-4" />
                    <p className="text-stone-500">No reviews yet. Be the first!</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Info Card */}
            <div className="glass-panel p-6 space-y-5">
              <h3 className="text-xs uppercase tracking-widest text-[#d4af37]">Visit Information</h3>
              
              {vineyard.address && (
                <div className="flex gap-3">
                  <MapPin className="w-5 h-5 text-stone-600 flex-shrink-0" />
                  <p className="text-stone-400 text-sm">{vineyard.address}</p>
                </div>
              )}
              
              {vineyard.visiting_hours && (
                <div className="flex gap-3">
                  <Clock className="w-5 h-5 text-stone-600 flex-shrink-0" />
                  <p className="text-stone-400 text-sm">{vineyard.visiting_hours}</p>
                </div>
              )}
              
              {vineyard.phone && (
                <div className="flex gap-3">
                  <Phone className="w-5 h-5 text-stone-600 flex-shrink-0" />
                  <a href={`tel:${vineyard.phone}`} className="text-stone-400 text-sm hover:text-[#d4af37]">
                    {vineyard.phone}
                  </a>
                </div>
              )}
              
              {vineyard.website && (
                <div className="flex gap-3">
                  <Globe className="w-5 h-5 text-stone-600 flex-shrink-0" />
                  <a
                    href={vineyard.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-stone-400 text-sm hover:text-[#d4af37] truncate"
                  >
                    {vineyard.website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
            </div>

            {/* Map Placeholder */}
            {(vineyard.latitude && vineyard.longitude) && (
              <div className="glass-panel p-4">
                <div className="aspect-square bg-stone-800 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="w-8 h-8 text-[#d4af37] mx-auto mb-2" />
                    <p className="text-stone-500 text-sm">
                      {vineyard.latitude.toFixed(4)}, {vineyard.longitude.toFixed(4)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Add Wine CTA */}
            {isAuthenticated && (
              <Button
                onClick={() => navigate(`/wines/new?vineyard=${id}`)}
                className="w-full bg-transparent border border-stone-700 text-stone-300 hover:border-[#d4af37] hover:text-[#d4af37] rounded-none uppercase tracking-widest text-xs py-5"
                data-testid="sidebar-add-wine-btn"
              >
                <Wine className="w-4 h-4 mr-2" />
                Add a Wine
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
          <DialogContent className="max-w-4xl bg-stone-950 border-stone-800 p-0">
            <img
              src={selectedPhoto}
              alt="Gallery"
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default VineyardDetail;
